import { Button, Flex, Link, Menu, MenuItem, MenuList, Spacer, Tag } from '@lokalise/louis';
import { parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';

import type { TaskTypeOption } from '@app/api/tasks';
import { splitFileName } from '@app/features/style-guide';
import { useTheme } from '@app/styled';
import track, { AnalyticsEventName } from '@app/utils/analytics';
import { formatDate } from '@app/utils/i18n';
import convertNewLineToBreakTag from '@app/utils/react/convertNewLineToBreakTag';
import makeUrlClickable from '@app/utils/react/makeUrlClickable';

import TaskLanguage from './TaskLanguage';
import { Description, LanguagesTable, MetaDataItem, Metadata, TableHeading, TaskContainer, Title } from './styles';
import { getProjectUrl } from './taskRouteHelper';
import type { Language, LanguageStatus, TaskStyleGuide, Task as TaskType } from './taskTypes';

const resolveStatus = (language: Language): LanguageStatus => {
    const progress = language.progress || 0;

    if (language.status === 'created' && progress > 0) {
        // For some reason project task lang status is not correctly updated to "in progress". So instead
        // we need to rely on `created` status and progress value is not 0.
        return 'in_progress';
    }

    return language.status;
};

const statusSortOrder = ['in_progress', 'created', 'completed'];

export type TaskProps = {
    task: TaskType & {
        langs: readonly Language[];
        task_type: TaskTypeOption;
        due_date: string | null;
        title: string;
        project_name: string;
        source_lang_cc_iso: string;
        source_lang_name: string;
        description?: string;
        created_by_fullname: string;
        team_id: number;
        style_guides: TaskStyleGuide[];
    };
};

const Task = ({ task }: TaskProps) => {
    const theme = useTheme();

    const taskLanguages = useMemo(
        () =>
            task.langs
                .filter((language) => language.status !== 'queued')
                .map((lang): Language => ({ ...lang, status: resolveStatus(lang) }))
                .sort((a, b) => {
                    if (a.status === 'in_progress' && a.progress === 100) {
                        return -1;
                    }

                    if (b.status === 'in_progress' && b.progress === 100) {
                        return 1;
                    }

                    if (a.status === b.status) {
                        // Order by lang id
                        return a.name < b.name ? 1 : -1;
                    }

                    // The index of array will serve as sorting order so if we get `in_progress` for "a" and `created`
                    // for b, they will have `0` and `1` indices. So `0 - 1` will result in `-1` which means
                    // "a" will be sorted before "b".
                    return statusSortOrder.indexOf(a.status) - statusSortOrder.indexOf(b.status);
                }),
        [task.langs],
    );

    const onDownloadStyleGuide = useCallback(
        (styleGuide?: TaskStyleGuide) => {
            if (styleGuide === undefined) {
                return;
            }

            window.open(`/style-guide/${task.team_id}/download/${styleGuide.id}/link`, '_blank', 'noopener,noreferrer');
        },
        [task.team_id],
    );

    return (
        <TaskContainer id={`task:${task.id}`} data-task-id={task.id}>
            <Flex direction="row" gap={1} align="center">
                <Flex direction="row" gap={1} grow={1}>
                    <Tag color={theme.color.custom.tasks.tag[task.task_type]}>{task.task_type.toLocaleUpperCase()}</Tag>
                    {task.due_date && (
                        <Tag color={theme.color.custom.tasks.tag.due}>
                            DUE {formatDate(parseISO(task.due_date), 'yyyy-MM-dd')}
                        </Tag>
                    )}
                </Flex>
                {task.style_guides.length > 0 && (
                    <Flex direction="row" grow={0}>
                        {task.style_guides.length === 1 ? (
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => onDownloadStyleGuide(task.style_guides[0])}
                            >
                                Download style guide
                            </Button>
                        ) : (
                            <Menu
                                menuButton={
                                    <Button variant="secondary" size="sm">
                                        Download style guide
                                    </Button>
                                }
                            >
                                <MenuList>
                                    {task.style_guides.map((styleGuide) => (
                                        <MenuItem key={styleGuide.id} onClick={() => onDownloadStyleGuide(styleGuide)}>
                                            {splitFileName(styleGuide.name).name}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Menu>
                        )}
                    </Flex>
                )}
            </Flex>
            <Spacer top={1}>
                <Title>
                    <Link
                        href={getProjectUrl(task, 'multi')}
                        onClick={() => {
                            track(AnalyticsEventName.TASK_WIDGET_TASK_NAME_CLICKED, { task_name: task.title });
                        }}
                    >
                        {task.title}
                    </Link>
                </Title>

                {task.description && (
                    <Description>{convertNewLineToBreakTag(makeUrlClickable(task.description))}</Description>
                )}
            </Spacer>
            <LanguagesTable>
                <thead>
                    <tr>
                        <TableHeading>Languages</TableHeading>
                        <TableHeading align="right">Words to do</TableHeading>
                        <TableHeading align="right">Progress</TableHeading>
                        <TableHeading>Status</TableHeading>
                    </tr>
                </thead>
                <tbody>
                    {taskLanguages.map((language) => (
                        <TaskLanguage key={language.lang_id} language={language} task={task} />
                    ))}
                </tbody>
            </LanguagesTable>
            <Metadata>
                <MetaDataItem data-test-id="source-lang">Source: {task.source_lang_name}</MetaDataItem>
                <MetaDataItem data-test-id="author-name">{task.created_by_fullname}</MetaDataItem>
                <MetaDataItem data-test-id="project-name">
                    In {task.project_name}
                    {task.master_branching_enabled === '1' && (
                        <Tag color={theme.color.text.light.default} size="small">
                            {task.branch_name ? task.branch_name : 'master'}
                        </Tag>
                    )}
                </MetaDataItem>
            </Metadata>
        </TaskContainer>
    );
};

export default Task;
